import { type AuthClaims, parseClaims } from '@fundline/shared/auth/claims';
import type { Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { supabase } from './supabase.ts';

export type AuthStatus = 'loading' | 'signed_out' | 'signed_in';

export interface AuthState {
  status: AuthStatus;
  session: Session | null;
  /** Fundline claims from the access token (workspace, role, platform owner). */
  claims: AuthClaims | null;
}

export interface AuthApi extends AuthState {
  /** Gets a fresh token so the access token hook runs again (after joining a workspace). */
  refresh: () => Promise<void>;
  /** Signs out on this device only. */
  signOut: () => Promise<void>;
}

function decodePayload(token: string): unknown {
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

function stateFrom(session: Session | null): AuthState {
  if (!session) return { status: 'signed_out', session: null, claims: null };
  return { status: 'signed_in', session, claims: parseClaims(decodePayload(session.access_token)) };
}

const AuthContext = createContext<AuthApi | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading', session: null, claims: null });
  const queryClient = useQueryClient();

  useEffect(() => {
    const client = supabase();
    let active = true;
    void client.auth.getSession().then(({ data }) => {
      if (active) setState(stateFrom(data.session));
    });
    const { data } = client.auth.onAuthStateChange((_event, session) => {
      // Called synchronously inside Supabase's auth lock: only update state here.
      setState(stateFrom(session));
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  const refresh = useCallback(async () => {
    const { data, error } = await supabase().auth.refreshSession();
    if (error) throw error;
    setState(stateFrom(data.session));
  }, []);

  const signOut = useCallback(async () => {
    await supabase().auth.signOut({ scope: 'local' });
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo(() => ({ ...state, refresh, signOut }), [state, refresh, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthApi {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Signed-in user with an active workspace (the app shell guarantees it on workspace routes). */
export function useWorkspaceClaims(): AuthClaims & { workspaceId: string; role: 'admin' | 'rep' } {
  const { claims } = useAuth();
  if (!claims?.workspaceId || !claims.role) throw new Error('No active workspace in the session');
  return { ...claims, workspaceId: claims.workspaceId, role: claims.role };
}
